// Each deploy replaces the hashed chunks under /assets. A tab opened before
// the deploy still asks for the old names, and the import fails. Reloading
// picks up the new build. A session marker limits recovery to one reload per
// tab, even if a chunk remains broken.
//
// The event is never cancelled: Vite resolves a cancelled import to undefined,
// so the page would render `undefined.ReportsPage` before the reload lands.
// Left alone, the import rejects and the route's error boundary waits it out.
const RELOAD_KEY = 'lionshare:stale-chunk-reload-at';
// How long this document counts as being replaced after it asked to reload. A
// page still alive after that (the user stayed on a "Leave site?" prompt) has
// its later failures reported again.
const RELOAD_GRACE_MS = 10_000;
let reloadStartedAt: number | null = null;
const reloadedFor = new WeakSet<Error>();

function reloadUnderWay(now: number): boolean {
  return reloadStartedAt !== null && now - reloadStartedAt < RELOAD_GRACE_MS;
}

/**
 * True for a failed import this tab is reloading to replace right now. Once the
 * grace has passed the page survived its reload, so the same error, which React
 * and the browser both keep for a failed module, is reported again.
 */
export function isStaleChunkReloadError(error: unknown): boolean {
  return error instanceof Error && reloadedFor.has(error) && reloadUnderWay(Date.now());
}

export function shouldReloadForStaleChunk(lastReloadAt: string | null): boolean {
  const last = Number(lastReloadAt);
  return !Number.isFinite(last) || lastReloadAt === null;
}

function isChunkFetchFailure(error: unknown): error is Error {
  return error instanceof Error
    && /^(?:Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module)\b/.test(error.message);
}

export function installStaleChunkReload(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const error = event.payload;
    if (!isChunkFetchFailure(error)) return;
    const now = Date.now();
    let shouldReload: boolean;
    try {
      shouldReload = shouldReloadForStaleChunk(sessionStorage.getItem(RELOAD_KEY));
      if (shouldReload) sessionStorage.setItem(RELOAD_KEY, String(now));
    } catch {
      // Without a persistent guard, leave the original failure reported.
      return;
    }
    if (shouldReload) {
      reloadStartedAt = now;
      reloadedFor.add(error);
      window.location.reload();
      return;
    }
    // A second chunk failing while this page's own reload is under way, such as
    // a click after the hover prefetch already failed, belongs to that reload.
    if (reloadUnderWay(now)) reloadedFor.add(error);
  });
}
